import type { ChatContentItemProps } from './types';
import {
  SystemMessageAddUser,
  SystemMessageCall,
  SystemMessageGroupCreated,
  SystemMessageRemoveUser,
  SystemMessageVideo,
} from 'Components/Chat/ChatLayout/SystemMessage';
import { parseMessageDate } from 'Components/Chat/utils';
import { MessageBubble } from 'Components/shared/MessageBubble';
import { isEmpty } from 'lodash';
import { MobXProviderContext, observer } from 'mobx-react';
import React from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { formatNumberWithNationalCode } from 'Utils/phoneUtil';
import { cn } from '~/lib/utils';
import { Avatar } from './Avatar';
import { Styled } from './index.styles';

export const ChatContentItem: React.FC<ChatContentItemProps> = observer(
  ({
    conversationId,
    loadPersonByIdGetIfMissingGet,
    selectUnreadCounts,
    message,
    selectPersonPresenceStatus,
  }) => {
    const {
      person: {
        getAllContactsWithSameNum,
        loggedInPersonId,
        selectPersonValueById,
      },
    } = React.useContext<RootStoreProps>(MobXProviderContext);

    const fetchPerson = (personId: number) => {
      // Trigger fetching the person if not already loaded
      void loadPersonByIdGetIfMissingGet(personId.toString());
    };

    const event = message.systemEvent?.eventType;
    const shouldRender =
      ([
        'Conversation.Created',
        'Conversation.Participants.Removed',
        'Conversation.Participants.Added',
      ].includes(event) ||
        !event) &&
      message?.conference?.provider !== 'Voxeet';

    React.useEffect(() => {
      if (message.personId > 0) {
        fetchPerson(
          message?.systemEvent?.payload?.personId || message.personId
        );
      }
    }, [message.personId, message.phone]);

    // Get the person directly from the MobX store - this will reactively update
    const personId =
      message?.systemEvent?.payload?.personId || message.personId;
    const person = personId > 0 ? selectPersonValueById(personId) : null;

    const contact = getAllContactsWithSameNum(message.phone)?.case({
      fulfilled: (contacts) => {
        return contacts[0];
      },
    });

    if (!shouldRender) {
      return null;
    }

    const parsedMessageDate = parseMessageDate(message.created);
    const presence =
      person?.sourceId && selectPersonPresenceStatus(person.sourceId);

    return (
      <Styled.FeedEvent id={message.id}>
        <div
          className={cn('content', {
            'content-grouped': !event,
            'flex-row-reverse': Boolean(message.conference || message.call),
          })}
        >
          {(!isEmpty(message.chat) ||
            !isEmpty(message.gif) ||
            !isEmpty(message.call) ||
            message.conference ||
            (message.documents && isEmpty(message.sms)) ||
            message.isDeleted) &&
            person && (
              <MessageBubble
                data-testid="messageBubble-message"
                AvatarComponent={
                  <Avatar
                    key={`message-bubble-avatar-${person?.id}-${presence?.state}`}
                    message={message}
                    person={person}
                    conversationId={conversationId}
                    selectUnreadCounts={selectUnreadCounts}
                    selectPersonPresenceStatus={selectPersonPresenceStatus}
                    isInactive={person.deleted}
                  />
                }
                messageDate={parsedMessageDate}
                isOwnMessage={loggedInPersonId === message.personId}
                content={message.chat?.text}
                displayName={`${person?.name}`}
                isInactive={person.deleted}
                {...{ message, conversationId }}
              />
            )}
          {message?.sms && (
            <MessageBubble
              data-testid="messageBubble-sms"
              AvatarComponent={
                <Avatar
                  {...{
                    message,
                    person: contact,
                    conversationId,
                    selectUnreadCounts,
                  }}
                  {...(!person && !contact && { variant: 'phone' })}
                  selectPersonPresenceStatus={selectPersonPresenceStatus}
                />
              }
              messageDate={parsedMessageDate}
              isOwnMessage={loggedInPersonId === message.personId}
              content={message.sms.text || ''}
              displayName={
                person
                  ? `${person.name}`
                  : contact?.name || formatNumberWithNationalCode(message.phone)
              }
              {...{ message, conversationId }}
            />
          )}
          {(person || contact) && (
            <>
              {message.systemEvent?.eventType === 'Conversation.Created' && (
                <SystemMessageGroupCreated {...{ message }} />
              )}
              {message.systemEvent?.eventType ===
                'Conversation.Participants.Removed' && (
                <SystemMessageRemoveUser {...{ message }} />
              )}
              {message.systemEvent?.eventType ===
                'Conversation.Participants.Added' && (
                <SystemMessageAddUser {...{ message }} />
              )}
              {message.conference && <SystemMessageVideo {...{ message }} />}
            </>
          )}
          {message.call?.disposition && <SystemMessageCall {...{ message }} />}
        </div>
      </Styled.FeedEvent>
    );
  }
);
